import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CartProvider } from './contexts/CartContext';
import { ProductsProvider } from './contexts/ProductsContext';
import { OrderProvider } from './contexts/OrderContext';
import { CustomerProvider } from './contexts/CustomerContext';
import { MarketingProvider } from './contexts/MarketingContext';
import { TimeSlotProvider } from './contexts/TimeSlotContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ReferralProvider } from './contexts/ReferralContext';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';
import BottomNavigation from './components/navigation/BottomNavigation';
import ErrorBoundary from './components/ErrorBoundary';
import ConnectionStatus from './components/ConnectionStatus';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmDialog';
import lazyRetry, { prefetchCriticalRoutes } from './utils/lazyRetry';
import RequireAdmin from './components/RequireAdmin';

const criticalAdminPages = [
  () => import('./pages/Dashboard'),
  () => import('./pages/Siparisler'),
];
const criticalPublicPages = [
  () => import('./pages/Urunler'),
  () => import('./pages/AnaSayfa'),
];

const DistributorProvider = React.lazy(() => import('./contexts/DistributorContext').then(m => ({ default: m.DistributorProvider })));
const DailyNotesProvider = React.lazy(() => import('./contexts/DailyNotesContext').then(m => ({ default: m.DailyNotesProvider })));
const AuditLogProvider = React.lazy(() => import('./contexts/AuditLogContext').then(m => ({ default: m.AuditLogProvider })));

const AnaSayfa = lazyRetry(() => import('./pages/AnaSayfa'));
const Urunler = lazyRetry(() => import('./pages/Urunler'));
const Hakkimizda = lazyRetry(() => import('./pages/Hakkimizda'));
const Iletisim = lazyRetry(() => import('./pages/Iletisim'));
const Giris = lazyRetry(() => import('./pages/Giris'));
const Sepet = lazyRetry(() => import('./pages/Sepet'));
const SepetTeslimat = lazyRetry(() => import('./pages/SepetTeslimat'));
const SepetOnay = lazyRetry(() => import('./pages/SepetOnay'));
const Hesabim = lazyRetry(() => import('./pages/Hesabim'));
const TekTikSiparis = lazyRetry(
  () => import(/* webpackChunkName: "tek-tik-siparis" */ './pages/TekTikSiparis')
);
const Kampanyalar = lazyRetry(() => import('./pages/Kampanyalar'));
const Kvkk = lazyRetry(() => import('./pages/Kvkk'));
const UyelikSozlesmesi = lazyRetry(() => import('./pages/UyelikSozlesmesi'));

const OperatorSiparisler = lazyRetry(() => import('./pages/operator/OperatorSiparisler'));
const OperatorDefter     = lazyRetry(() => import('./pages/operator/OperatorDefter'));
const OperatorFiyatlar   = lazyRetry(() => import('./pages/operator/OperatorFiyatlar'));

const Dashboard = lazyRetry(() => import('./pages/Dashboard'));
const ProductManagement = lazyRetry(() => import('./pages/ProductManagement'));
const AdminKampanyalar = lazyRetry(() => import('./pages/AdminKampanyalar'));
const SiparislerSayfasi = lazyRetry(() => import('./pages/Siparisler'));
const MusteriYonetimi = lazyRetry(() => import('./pages/MusteriYonetimi'));
const Raporlar = lazyRetry(() => import('./pages/Raporlar'));
const Defter = lazyRetry(() => import('./pages/Defter'));
const AuditLog = lazyRetry(() => import('./pages/AuditLog'));

const LoadingSpinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
    <div style={{ color: '#334155', fontSize: '18px', textAlign: 'center' }}>Yükleniyor...</div>
  </div>
);

// Admin sayfaları için wrapper — Marketing ve TimeSlot burada yükleniyor (AdminKampanyalar + Raporlar ihtiyacı)
const AdminProvidersWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<LoadingSpinner />}>
    <DistributorProvider>
      <DailyNotesProvider>
        <AuditLogProvider>
          <MarketingProvider>
            <TimeSlotProvider>
              {children}
            </TimeSlotProvider>
          </MarketingProvider>
        </AuditLogProvider>
      </DailyNotesProvider>
    </DistributorProvider>
  </Suspense>
);

// /hesabim — Subscription ve Referral yalnızca bu route'da gerekli
const HesabimRoute = () => (
  <SubscriptionProvider>
    <ReferralProvider>
      <Hesabim />
    </ReferralProvider>
  </SubscriptionProvider>
);

// /sepet/teslimat — zaman dilimi seçimi için TimeSlot yalnızca bu route'da gerekli
const SepetTeslimatRoute = () => (
  <TimeSlotProvider>
    <SepetTeslimat />
  </TimeSlotProvider>
);

const AppWithShortcuts = () => {
  useKeyboardShortcuts();
  const location = useLocation();

  const showBottomNav = React.useMemo(() => {
    return !location.pathname.startsWith('/admin');
  }, [location.pathname]);

  React.useEffect(() => {
    const isAdmin = location.pathname.startsWith('/admin');
    prefetchCriticalRoutes(isAdmin ? criticalAdminPages : criticalPublicPages);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<AnaSayfa />} />
          <Route path="/urunler" element={<Urunler />} />
          <Route path="/hakkimizda" element={<Hakkimizda />} />
          <Route path="/iletisim" element={<Iletisim />} />
          <Route path="/kampanyalar" element={<Kampanyalar />} />
          <Route path="/kvkk" element={<Kvkk />} />
          <Route path="/uyelik-sozlesmesi" element={<UyelikSozlesmesi />} />
          <Route path="/giris" element={<Giris />} />
          <Route path="/hesabim" element={<HesabimRoute />} />
          <Route path="/sepet" element={<Sepet />} />
          <Route path="/sepet/teslimat" element={<SepetTeslimatRoute />} />
          <Route path="/sepet/onay" element={<SepetOnay />} />
          <Route path="/tek-tik-siparis" element={<TekTikSiparis />} />
          <Route path="/tek-tik-siparis/:id" element={<TekTikSiparis />} />

          {/* Operator Routes */}
          <Route path="/operator" element={<Navigate to="/operator/siparisler" replace />} />
          <Route path="/operator/siparisler" element={<RequireAdmin><OperatorSiparisler /></RequireAdmin>} />
          <Route path="/operator/defter"     element={<RequireAdmin><OperatorDefter /></RequireAdmin>} />
          <Route path="/operator/fiyatlar"   element={<RequireAdmin><OperatorFiyatlar /></RequireAdmin>} />

          {/* Admin Routes */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" />} />
          <Route path="/admin/login" element={<Navigate to="/giris?tab=admin" replace />} />
          <Route path="/admin/dashboard" element={<RequireAdmin><AdminProvidersWrapper><Dashboard /></AdminProvidersWrapper></RequireAdmin>} />
          <Route path="/admin/products" element={<RequireAdmin><AdminProvidersWrapper><ProductManagement /></AdminProvidersWrapper></RequireAdmin>} />
          <Route path="/admin/campaigns" element={<RequireAdmin><AdminProvidersWrapper><AdminKampanyalar /></AdminProvidersWrapper></RequireAdmin>} />
          <Route path="/admin/orders" element={<RequireAdmin><AdminProvidersWrapper><SiparislerSayfasi /></AdminProvidersWrapper></RequireAdmin>} />
          <Route path="/admin/customers" element={<RequireAdmin><AdminProvidersWrapper><MusteriYonetimi /></AdminProvidersWrapper></RequireAdmin>} />
          <Route path="/admin/reports" element={<RequireAdmin><AdminProvidersWrapper><Raporlar /></AdminProvidersWrapper></RequireAdmin>} />
          <Route path="/admin/defter" element={<RequireAdmin><AdminProvidersWrapper><Defter /></AdminProvidersWrapper></RequireAdmin>} />
          <Route path="/admin/audit-log" element={<RequireAdmin><AdminProvidersWrapper><AuditLog /></AdminProvidersWrapper></RequireAdmin>} />
        </Routes>

        {showBottomNav && <BottomNavigation />}
        <ConnectionStatus />
      </Suspense>
    </ErrorBoundary>
  );
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <ToastProvider>
        <ConfirmProvider>
        <AuthProvider>
          <NotificationProvider>
            <CartProvider>
              <ProductsProvider>
                <CustomerProvider>
                  <OrderProvider>
                    <Router>
                      <AppWithShortcuts />
                    </Router>
                  </OrderProvider>
                </CustomerProvider>
              </ProductsProvider>
            </CartProvider>
          </NotificationProvider>
        </AuthProvider>
        </ConfirmProvider>
      </ToastProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
